import React, { useEffect, useMemo, useState } from 'react';
import { apiService, SuperuserPrimeReport } from '../../services/api';

type PrimeReportFormState = {
  title: string;
  description: string;
  token_cost: string;
  category: string;
  google_drive_link: string;
  is_active: boolean;
};

const emptyForm: PrimeReportFormState = {
  title: '',
  description: '',
  token_cost: '',
  category: '',
  google_drive_link: '',
  is_active: true,
};

const SuperuserPrimeReportsPanel: React.FC = () => {
  const [reports, setReports] = useState<SuperuserPrimeReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingReport, setEditingReport] = useState<SuperuserPrimeReport | null>(null);
  const [form, setForm] = useState<PrimeReportFormState>(emptyForm);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.fetchSuperuserPrimeReports();
      setReports(data);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load prime reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const filteredReports = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return reports;
    return reports.filter((report) =>
      report.title.toLowerCase().includes(term) ||
      (report.description || '').toLowerCase().includes(term) ||
      (report.category || '').toLowerCase().includes(term)
    );
  }, [reports, search]);

  const resetForm = () => {
    setEditingReport(null);
    setForm(emptyForm);
  };

  const startCreate = () => {
    setEditingReport(null);
    setForm(emptyForm);
  };

  const startEdit = (report: SuperuserPrimeReport) => {
    setEditingReport(report);
    setForm({
      title: report.title || '',
      description: report.description || '',
      token_cost: String(report.token_cost ?? ''),
      category: report.category || '',
      google_drive_link: report.google_drive_link || '',
      is_active: Boolean(report.is_active),
    });
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!form.token_cost.trim()) {
      setError('Token cost is required.');
      return;
    }
    if (!form.google_drive_link.trim()) {
      setError('Google Drive link is required.');
      return;
    }
    const tokenCost = Number(form.token_cost);
    if (!Number.isFinite(tokenCost) || tokenCost < 0) {
      setError('Token cost must be a valid non-negative number.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        token_cost: tokenCost,
        category: form.category.trim(),
        google_drive_link: form.google_drive_link.trim(),
        is_active: form.is_active,
      };
      if (editingReport) {
        await apiService.updateSuperuserPrimeReport(editingReport.id, payload);
      } else {
        await apiService.createSuperuserPrimeReport(payload);
      }
      resetForm();
      await loadReports();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (report: SuperuserPrimeReport) => {
    const confirmed = window.confirm(`Deactivate prime report "${report.title}"?`);
    if (!confirmed) return;
    try {
      setError(null);
      await apiService.deleteSuperuserPrimeReport(report.id);
      await loadReports();
      if (editingReport?.id === report.id) resetForm();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to delete report');
    }
  };

  return (
    <section className="bg-white rounded-2xl shadow-card border border-slate-200 p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Prime Reports</h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage all prime reports, including create, edit, and deactivate.
          </p>
        </div>
        <button
          type="button"
          onClick={startCreate}
          className="inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm"
        >
          Create Prime Report
        </button>
      </div>

      <form onSubmit={handleSave} className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          value={form.title}
          onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
          placeholder="Report title"
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
          required
        />
        <input
          value={form.token_cost}
          onChange={(e) => setForm((prev) => ({ ...prev, token_cost: e.target.value }))}
          placeholder="Token cost"
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
          inputMode="numeric"
          required
        />
        <input
          value={form.category}
          onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
          placeholder="Category"
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
        />
        <input
          value={form.google_drive_link}
          onChange={(e) => setForm((prev) => ({ ...prev, google_drive_link: e.target.value }))}
          placeholder="Google Drive link"
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
          required
        />
        <textarea
          value={form.description}
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          placeholder="Description"
          className="md:col-span-2 px-3 py-2 rounded-lg border border-slate-200 text-sm min-h-[80px]"
        />
        <label className="inline-flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
            className="rounded border-slate-300"
          />
          Active report
        </label>
        <div className="md:col-span-2 flex items-center gap-2">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
          >
            {saving ? 'Saving...' : editingReport ? 'Update Report' : 'Create Report'}
          </button>
          {editingReport && (
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search reports..."
          className="w-full sm:w-80 px-3 py-2 rounded-lg border border-slate-200 text-sm"
        />
        <p className="text-xs text-slate-500">
          Showing {filteredReports.length} of {reports.length}
        </p>
      </div>

      {error && (
        <div className="mt-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-3 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <p className="mt-4 text-sm text-slate-500">Loading prime reports...</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2 text-left">Title</th>
                <th className="py-2 text-left">Category</th>
                <th className="py-2 text-right">Tokens</th>
                <th className="py-2 text-right">Purchases</th>
                <th className="py-2 text-right">Revenue</th>
                <th className="py-2 text-center">Status</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.map((report) => (
                <tr key={report.id} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 pr-3">
                    <p className="font-semibold text-slate-900">{report.title}</p>
                    {report.description ? (
                      <p className="text-xs text-slate-500 line-clamp-2">{report.description}</p>
                    ) : null}
                  </td>
                  <td className="py-2 text-slate-600">{report.category || '—'}</td>
                  <td className="py-2 text-right">{report.token_cost.toLocaleString()}</td>
                  <td className="py-2 text-right">{report.purchase_count.toLocaleString()}</td>
                  <td className="py-2 text-right">{report.total_revenue.toLocaleString()}</td>
                  <td className="py-2 text-center">
                    <span
                      className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                        report.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {report.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-2">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(report)}
                        className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(report)}
                        className="px-3 py-1.5 rounded-lg border border-rose-200 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                      >
                        Deactivate
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredReports.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-sm text-slate-500">
                    No prime reports found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default SuperuserPrimeReportsPanel;
